import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import '../../styles/Forms.css';

const AddDepartmentPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    site: '',
    manager: '',
    isActive: true,
  });

  useEffect(() => {
    fetchSitesAndUsers();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchDepartment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSitesAndUsers = async () => {
    try {
      const [sitesRes, usersRes] = await Promise.all([
        api.getSites({ isActive: 'true' }),
        api.getUsers({ limit: 1000 }),
      ]);

      if (sitesRes.success) setSites(sitesRes.data || []);
      if (usersRes.success) setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching sites/users:', error);
    }
  };

  const fetchDepartment = async () => {
    try {
      setLoading(true);
      const res = await api.getDepartment(id);
      if (res.success && res.data) {
        const dept = res.data;
        setFormData({
          name: dept.name || '',
          description: dept.description || '',
          site: dept.site?._id || dept.site || '',
          manager: dept.manager?._id || dept.manager || '',
          isActive: dept.isActive !== undefined ? dept.isActive : true,
        });
      } else {
        toast.error('Department not found');
        navigate('/admin/departments');
      }
    } catch (error) {
      console.error('Error loading department:', error);
      toast.error('Error loading department details');
      navigate('/admin/departments');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter department name');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        site: formData.site || undefined,
        manager: formData.manager || undefined,
        isActive: formData.isActive,
      };

      let res;
      if (isEdit) {
        res = await api.updateDepartment(id, payload);
      } else {
        res = await api.createDepartment(payload);
      }

      if (res.success) {
        toast.success(isEdit ? 'Department updated successfully!' : 'Department created successfully!');
        navigate(-1);
      } else {
        toast.error(
          (isEdit ? 'Error updating department: ' : 'Error creating department: ') +
            (res.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error(isEdit ? 'Error updating department:' : 'Error creating department:', error);
      toast.error(
        (isEdit ? 'Error updating department: ' : 'Error creating department: ') +
          (error.message || 'Unknown error')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-department-page" style={{ padding: '16px' }}>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '20px',
            marginBottom: '20px',
            maxWidth: '600px',
            margin: '0 auto 20px auto',
          }}
        >
          {/* Left column - basic info */}
          <div
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <h2
              style={{
                margin: '0 0 16px 0',
                fontSize: '16px',
                fontWeight: 600,
                color: '#2d3748',
              }}
            >
              Department Details
            </h2>

            <div className="form-group">
              <label htmlFor="name">Department Name *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter department name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Short description of this department"
                rows="4"
              />
            </div>

            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
              <input
                id="isActive"
                name="isActive"
                type="checkbox"
                checked={formData.isActive}
                onChange={handleInputChange}
                style={{ marginRight: '6px' }}
              />
              <label htmlFor="isActive" style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}>
                Active
              </label>
            </div>
          </div>

          {/* Right column - mappings */}
          {/* <div>
            <div
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '16px',
              }}
            >
              <h2
                style={{
                  margin: '0 0 16px 0',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2d3748',
                }}
              >
                Site & Manager
              </h2>

              <div className="form-group">
                <label htmlFor="site">Site</label>
                <select id="site" name="site" value={formData.site} onChange={handleInputChange}>
                  <option value="">Select Site</option>
                  {sites.map((site) => (
                    <option key={site._id || site.id} value={site._id || site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="manager">Department Manager</label>
                <select
                  id="manager"
                  name="manager"
                  value={formData.manager}
                  onChange={handleInputChange}
                >
                  <option value="">Select Manager</option>
                  {users.map((u) => (
                    <option key={u._id || u.id} value={u._id || u.id}>
                      {u.name} {u.email ? `(${u.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div> */}
        </div>

        {/* Form actions */}
        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
            paddingTop: '16px',
            borderTop: '1px solid #e2e8f0',
            maxWidth: '600px',
            margin: '0 auto',
          }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            style={{
              padding: '10px 20px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Department' : 'Create Department'}
          </button>
        </div>
      </form>

      {loading && <Loader />}
    </div>
  );
};

export default AddDepartmentPage;

